import api from './api';

/**
 * Service para gerenciar preços diferenciados de produtos por extensão
 */
class ProdutoPrecoService {
  /**
   * Buscar preço aplicável de um produto para uma extensão
   * @param {number} produtoId - ID do produto
   * @param {number|null} extensaoId - ID da extensão (opcional)
   * @returns {Promise<Object>} - Dados do preço
   */
  static async getPrecoAplicavel(produtoId, extensaoId = null) {
    try {
      const params = new URLSearchParams({ produtoId });
      if (extensaoId) {
        params.append('extensaoId', extensaoId);
      }
      
      const response = await api.get(`/produto-precos/preco-aplicavel?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar preço aplicável:', error);
      throw error;
    }
  }

  /**
   * Listar preços customizados com filtros
   * @param {Object} filters - Filtros (page, limit, produto, extensao, ativo, search)
   * @returns {Promise<Object>} - Lista paginada de preços
   */
  static async list(filters = {}) {
    try {
      const { page = 1, limit = 100, produto, extensao, ativo, search } = filters;
      
      // Criar params apenas com valores não vazios
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      if (produto) params.append('produto', produto);
      if (extensao) params.append('extensao', extensao);
      if (ativo !== undefined && ativo !== '') params.append('ativo', ativo);
      if (search) params.append('search', search);
      
      const url = `/produto-precos?${params}`;
      console.log('🌐 [Service] Chamando API:', url);
      console.log('🌐 [Service] Filtros:', filters);
      
      const response = await api.get(url);
      
      console.log('📦 [Service] Response status:', response.status);
      console.log('📦 [Service] Response data:', response.data);
      
      return response;
    } catch (error) {
      console.error('❌ [Service] Erro ao listar preços customizados:', error);
      console.error('❌ [Service] Error response:', error.response?.data);
      throw error;
    }
  }

  /**
   * Listar preços de um produto para todas as extensões
   * @param {number} produtoId - ID do produto
   * @returns {Promise<Array>} - Lista de extensões com preços
   */
  static async listarPorProduto(produtoId) {
    try {
      const response = await api.get(`/produto-precos/produto/${produtoId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao listar preços por produto:', error);
      throw error;
    }
  }

  /**
   * Buscar preço customizado por ID
   * @param {number} id - ID do preço customizado
   * @returns {Promise<Object>} - Dados do preço
   */
  static async getById(id) {
    try {
      const response = await api.get(`/produto-precos/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar preço customizado:', error);
      throw error;
    }
  }

  /**
   * Criar novo preço customizado
   * @param {Object} data - Dados do preço (produto, extensao, valor, ativo)
   * @returns {Promise<Object>} - Preço criado
   */
  static async create(data) {
    try {
      const response = await api.post('/produto-precos', data);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar preço customizado:', error);
      throw error;
    }
  }

  /**
   * Atualizar preço customizado
   * @param {number} id - ID do preço customizado
   * @param {Object} data - Dados a atualizar (valor, ativo)
   * @returns {Promise<Object>} - Preço atualizado
   */
  static async update(id, data) {
    try {
      const response = await api.put(`/produto-precos/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar preço customizado:', error);
      throw error;
    }
  }

  /**
   * Excluir preço customizado
   * @param {number} id - ID do preço customizado
   * @returns {Promise<Object>} - Resultado da exclusão
   */
  static async delete(id) {
    try {
      const response = await api.delete(`/produto-precos/${id}`);
      return response;
    } catch (error) {
      console.error('Erro ao excluir preço customizado:', error);
      throw error;
    }
  }
  /**
   * Criar múltiplos preços customizados em lote
   * @param {Object} data - Dados do cadastro em massa
   * @param {Array} data.produtos - Array de IDs de produtos
   * @param {Array} data.extensoes - Array de IDs de extensões
   * @param {string} data.valorTipo - "fixo" ou "percentual"
   * @param {number} data.valor - Valor fixo OU percentual
   * @param {boolean} data.ativo - Status (padrão true)
   * @returns {Promise<Object>} - Resultado do processamento em lote
   */
  static async createBulk(data) {
    try {
      console.log('🚀 [Service] Criando preços em lote:', data);
      const response = await api.post('/produto-precos/bulk', data);
      console.log('✅ [Service] Preços criados em lote:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Service] Erro ao criar preços em lote:', error);
      throw error;
    }
  }}

export default ProdutoPrecoService;
